import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery } from '@apollo/client'
import { fromJS, List, Map, Set } from 'immutable'
import castArray from 'lodash/castArray'
import flatMap from 'lodash/flatMap'
import get from 'lodash/get'
import includes from 'lodash/includes'
import isArray from 'lodash/isArray'
import parseInt from 'lodash/parseInt'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form } from 'formik'
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz'
import { getAuthPermissions, PERM_UPDATE_CONTENT } from 'services/auth'
import { TIMEZONE_MOUNTAIN } from 'services/constants'
import {
  getViewRecordsAsList,
  getRecords,
  getViewProcessing,
  filterRecords,
  getViewError,
} from 'services/data'
import {
  persistData as persistDataAction,
  getDataList as getDataListAction,
} from 'services/data/redux/actions'
import {
  CONTENT_DETAIL_APPLICATION_KEY,
  CONTENT_STORE_KEY,
  createContentDetailViewOptions,
  createContentMediaOptions,
  createContentTranslationsViewOptions,
  getFilteredDisplayOptions,
} from 'services/content'
import { getInitialValues, getValidationSchema } from 'services/content/fields'
import {
  CONTENT_TYPE_PROMOTION,
  CONTENT_TYPE_PRODUCT,
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_TAG,
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_PERSON,
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_BONUS_PACKAGE,
  CONTENT_TYPE_SERVICE,
} from 'services/content/content-types'
import { getContentReferenceTypesContentDictionary } from 'services/content-references'
import {
  CONTENT_REFERENCE_FIELDS,
  CONTENT_REFERENCE_FIELDS_ASYNC,
  CONTENT_REFERENCE_FIELDS_MAIN,
  CONTENT_REFERENCE_TYPE_CONTENT_TO_TYPES,
  CONTENT_REFERENCE_TYPE_LABELS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPE_FILTER_OPTIONS,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
  CONTENT_REFERENCE_FIELDS_YORE,
  YOGA_REFERENCE_FIELDS,
  MEDITATION_REFERENCE_FIELDS,
} from 'services/content-references/types'
import { BUNDLE_LABELS, BUNDLE_TYPES } from 'services/content/bundles'
import { getDisplayTypeOptions } from 'services/content/display-types'
import {
  getLanguageOptionsSupported,
  getLanguageLabel,
  getLanguageChipClass,
} from 'services/languages'
import { MEDIA_STORE_KEY } from 'services/media'
import MuiCheckbox from 'gui/components/common/MuiCheckbox'
import MuiSelectWrapper from 'gui/components/common/MuiSelect'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import MuiButton from 'gui/components/common/MuiButton'
import Box from '@mui/material/Box'
import ReactSelect from 'react-select'
import AsyncSelect from 'react-select/async'
import { Container, Grid, IconButton, Tooltip, MenuItem, Chip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import Link from 'gui/components/link'
import ProductionStatusSelect from 'gui/components/production-status-select'
import TextInputFormatSelect from 'gui/components/text-input-format-select'
import { renderModal as renderModalAction } from 'services/modal/redux/actions'
import { selectAuth, selectAuthRoles } from 'services/auth/redux/selectors'
import { ROLE_EDITOR_PLUS, ROLE_SITE_ADMIN } from 'services/auth/roles'
import { selectConfig } from 'services/config/redux/selectors'
import { selectData } from 'services/data/redux/selectors'
import { selectLocation } from 'services/location/redux/selectors'
// import { fetchTranslation as fetchTranslationAction } from 'services/content/redux/actions'
import { CONFIRM_MODAL } from 'services/modal'
import { CONTENT_POLICY_STORE_KEY } from 'services/content-policy'
import { isEmpty, map } from 'lodash'
import { showErrorToast } from 'services/toast/redux/actions'
import { useAsyncContentReferenceDisplay } from 'hooks/useAsyncContentReferenceDisplay'
import ContentEpisodes from './episodes'
import ContentImages from './content-images'
import ProductReferencesList from './product-references-list'
import VideoContentMedia from './video-content-media'
import AIMetadataGeneration from './ai-metadata-generation'
import AIMarketingPlanGeneration from './ai-marketing-plan-generation'
import PrimaryCourseSection from './primary-course-section'
// TODO These are duplicated for now, come back and make DRYer
import PRODUCT_TYPE_OPTIONS from './product-type-options'
import { getCurrentContentDetail } from './helpers'
import { ContentDetailLoader } from './content-detail-loader'
import ContentActions from './content-actions'
import { TextareaWithCounter } from '../textarea-with-counter'
import { CONTENT_LIST_QUERY, GET_SERIES_REFERENCES } from './queries'
import {
  REFERENCE_TYPE_KEYS_TO_POPULATE_FROM_METADATA,
  SINGLE_SELECT_REFERENCE_TYPES,
  MULTI_SELECT_REFERENCE_TYPES,
  BOOLEAN_FIELDS,
  NUMBER_FIELDS,
  TEXTAREA_FIELDS,
} from './constants'

import './index.scss'

/**
 * Converts an array of IDs to display objects using provided options
 * Used for populating select fields with proper display values
 * @param {Array} values - Array of IDs
 * @param {Array} options - Array of option objects with value and label
 * @returns {Array} Array of display objects with value and label properties
 */
const convertIdsToDisplayObjects = (values, options) => {
  if (!Array.isArray(values)) return []
  return values.map((value) => {
    const option = options.find((opt) => opt.value === value)
    return option || { value, label: value.toString() }
  })
}

/**
 * Transforms raw field values into the format required by React-Select components.
 * This is necessary because:
 * 1. React-Select expects values in {value, label} format
 * 2. Async fields store both the ID and display data
 * 3. Non-async fields only store IDs and need to be converted to display format
 *
 * @param {any} rawValue - The raw value from the form (could be ID, array of IDs, or {value, display} object)
 * @param {boolean} isMulti - Whether the field is multi-select
 * @param {boolean} isAsync - Whether the field is async (stores display data)
 * @param {Array} options - Available options for non-async fields
 * @returns {any} Value in format expected by React-Select
 */
const transformFieldValueForDisplay = (rawValue, isMulti, isAsync, options) => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return isMulti ? [] : null
  }

  // For async fields, use the stored display data
  if (isAsync) {
    return rawValue.display || (isMulti ? [] : null)
  }

  // For non-async fields, convert IDs to display objects
  if (isMulti) {
    return convertIdsToDisplayObjects(rawValue, options)
  }

  // For single-select fields, find the matching option
  const option = options?.find((opt) => opt.value === rawValue)
  return option || null
}

/**
 * Get the component state by combining all redux state
 * @param {Object} props The props
 * @param {Map} props.data The redux data state
 * @param {Map} props.location The redux location state
 * @returns The entire component state using props and combined redux state
 */
function getState (props) {
  const { data, location, storeKey } = props
  const content = getCurrentContentDetail(props)
  const options = createContentDetailViewOptions({ location })
  const contentRecord =
    getViewRecordsAsList({
      data,
      storeKey,
      options,
    }).first() || Map()

  const processing = getViewProcessing({
    data,
    storeKey,
    options,
  })
  const viewErrors = fromJS(
    flatMap([options, options.set('generatePath', false)], (o) => {
      const viewError = getViewError({ data, storeKey, options: o })
      return get(viewError, 'response.body.errors', [])
    }),
  )
  const formErrors = Map(viewErrors.map((e) => [e.get('detail').match(/^\S*/)[0], e.get('detail')]))
  const records = getViewRecordsAsList({
    data,
    storeKey,
    options: Map({
      primaryLanguageFallback: false,
      content: List([
        Map({ contentId: content.get('contentId'), contentType: content.get('contentType') }),
      ]),
    }),
  })
  const recordsTranslated = records.filter((v) => v.get('language') !== content.get('language'))
  const mediaOptions = createContentMediaOptions({ content })
  const processingMedia = getViewProcessing({
    data,
    storeKey: MEDIA_STORE_KEY,
    options: mediaOptions,
  })
  const processingTranslations = getViewProcessing({
    storeKey,
    data,
    options: createContentTranslationsViewOptions(content),
  })
  const references = content.get('references') || List()
  const seriesRef = references.find((r) => r.get('type') === 'series')
  const contentSeries =
    (seriesRef &&
      filterRecords(
        { data, storeKey },
        (v) =>
          v.get('contentId') === seriesRef.get('contentId') &&
          v.get('contentType') === seriesRef.get('contentType'),
      ).first()) ||
    Map()
  const contentOptionsFields = CONTENT_REFERENCE_TYPE_CONTENT_TO_TYPES
  const contentPolicyOptions = getViewRecordsAsList({
    data,
    storeKey: CONTENT_POLICY_STORE_KEY,
    options: Map({ limit: 500 }),
  })
    .map((cp) =>
      Map({ value: cp.get('policyId'), label: `${cp.get('policyId')} - ${cp.get('name')}` }),
    )
    .unshift(Map({ value: '', label: 'None' }))

  const enableMarketingCenters = props.config.getIn(['data', 'features', 'enableMarketingCenters'])
  const contentReferenceFields = CONTENT_REFERENCE_FIELDS.get(content.get('contentType', List()))
    .filter((v) => (enableMarketingCenters ? true : v !== CONTENT_REFERENCE_TYPE_MARKETING_CENTERS))
    .map((v) => {
      const contentReferenceOptions = getContentReferenceTypesContentDictionary().get(v)
      const isAsync = CONTENT_REFERENCE_FIELDS_ASYNC.get(
        content.get('contentType', List()),
      ).includes(v)
      const isMulti = MULTI_SELECT_REFERENCE_TYPES.includes(v)
      const fallbackRecords =
        getViewRecordsAsList({
          data,
          storeKey,
          options: Map({
            contentType: contentReferenceOptions.get('contentType'),
            bundle: contentReferenceOptions.get('bundle'),
            filterType: contentReferenceOptions.get('filterType'),
            primaryLanguageFallback: true,
            sort: 'alpha',
          }),
        }) || List()
      const mapToOption = (m) => ({ label: m.get('title'), value: m.get('contentId') })
      return Map({
        name: v,
        label: contentReferenceOptions.get('label', v),
        type: contentReferenceOptions.get('contentType'),
        getValue: (values) => {
          // For async fields, use the stored display data
          if (isAsync) {
            if (!values) return isMulti ? [] : null
            return values.display || (isMulti ? [] : null)
          }

          // For non-async fields, convert IDs to display objects
          const defaultValues = filterRecords({ data, storeKey }, (c) =>
            includes(castArray(values), c.get('contentId')),
          )
            .map(mapToOption)
            .toList()
            .toSet()
          return isArray(values) ? defaultValues.toArray() : defaultValues.first()
        },
        isAsync,
        options: !isAsync ? fallbackRecords.map(mapToOption).toArray() : [],
      })
    })
  return {
    viewErrors,
    formErrors,
    processing,
    processingMedia,
    processingTranslations,
    content,
    contentSeries,
    contentRecord,
    recordsTranslated,
    contentReferenceFields,
    contentOptionsFields,
    contentPolicyOptions,
    enableMarketingCenters,
  }
}

/**
 * Create the text for the content series field
 * @param {import('immutable').Map} contentSeries The content series
 * @returns {String} The content series text
 */
function createSeriesText (contentSeries) {
  if (!contentSeries || contentSeries.isEmpty()) {
    return ''
  }
  return `${contentSeries.get('title')} [${contentSeries.get('contentId')}:series]`
}

function isEpisode (formValues) {
  const episodeProductTypes = Set(['episode', 'segment'])
  return episodeProductTypes.includes(formValues.productType)
}

// Render Helper Functions
const renderProductTypeOptions = (content) =>
  PRODUCT_TYPE_OPTIONS.get(content.get('contentType'), Map()).map((option) => (
    <MenuItem value={option.get('value')} key={`product-type-${option.get('value')}`}>
      {option.get('label')}
    </MenuItem>
  ))

const renderFilterTypeOptions = (contentType, enableMarketingCenters) =>
  CONTENT_REFERENCE_TYPE_CONTENT_TO_TYPES.filter((v) => v === contentType)
    .filter((v, k) =>
      enableMarketingCenters ? true : k !== CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
    )
    .sortBy((_v, type) => CONTENT_REFERENCE_TYPE_LABELS.get(type, ''))
    .map((_v, option) => ({ label: CONTENT_REFERENCE_TYPE_LABELS.get(option, ''), value: option }))
    .filter((v) => v.label !== '')
    .toList()
    .unshift({ label: 'None', value: null })
    .toArray()

const renderBundleOptions = (contentType) =>
  BUNDLE_TYPES.filter((v) => v === contentType)
    .sortBy((_v, type) => BUNDLE_LABELS.get(type, ''))
    .map((_v, option) => ({ label: BUNDLE_LABELS.get(option, ''), value: option }))
    .filter((v) => v.label !== '')
    .toList()
    .unshift({ label: 'None', value: null })
    .toArray()

const renderDisplayTypeOptions = (contentType) =>
  getDisplayTypeOptions(contentType).map((option) => (
    <MenuItem value={option.get('value')} key={`display-type-${option.get('value')}`}>
      {option.get('label')}
    </MenuItem>
  ))

const renderContentPolicyOptions = (options) =>
  options.map((option) => (
    <MenuItem value={option.get('value')} key={`content-policy-${option.get('value')}`}>
      {option.get('label')}
    </MenuItem>
  ))

const renderLanguageOptions = () =>
  getLanguageOptionsSupported().map((option) => (
    <MenuItem value={option.get('value')} key={`language-${option.get('value')}`}>
      {option.get('label')}
    </MenuItem>
  ))

function AsyncContentReferenceSelect (props) {
  const { name, value, isDisabled, isMulti, loadOptions, onChange, onBlur } = props

  return (
    <AsyncSelect
      name={name}
      value={value}
      isDisabled={isDisabled}
      isMulti={isMulti}
      cacheOptions
      isClearable
      loadOptions={loadOptions}
      onChange={onChange}
      onBlur={onBlur}
      blurInputOnSelect={true}
    />
  )
}

function renderContentReferenceField (field, values, errors, props) {
  const { isDisabled, handleChange, handleBlur, contentReferenceQuery, setFieldValue } = props
  const name = field.get('name')
  const label = field.get('label')
  const isAsync = field.get('isAsync')
  const isMulti = !SINGLE_SELECT_REFERENCE_TYPES.includes(name)
  const options = field.get('options')

  const fieldValue = transformFieldValueForDisplay(values[name], isMulti, isAsync, options)

  const loadOptions = async (value) => {
    const contentType = field.get('type').first()
    const bundle = getContentReferenceTypesContentDictionary().getIn([name, 'bundle'])

    const titles = await contentReferenceQuery(value, bundle, contentType)
    const mappedOptions = map(titles, (c) => ({
      label: get(c, 'title'),
      value: parseInt(get(c, 'contentId')),
    }))

    return mappedOptions
  }

  // Updated onChange handler for async fields
  const handleSelectChange = (selectedOptions) => {
    if (isAsync) {
      if (isMulti) {
        const valuesArr = selectedOptions ? selectedOptions.map((opt) => opt.value) : []
        const display = selectedOptions || []
        setFieldValue(name, { value: valuesArr, display })
      } else {
        setFieldValue(
          name,
          selectedOptions
            ? { value: selectedOptions.value, display: selectedOptions }
            : { value: null, display: null },
        )
      }
    } else if (isMulti) {
      handleChange({
        target: { name, value: selectedOptions ? selectedOptions.map((opt) => opt.value) : [] },
      })
    } else {
      handleChange({ target: { name, value: selectedOptions ? selectedOptions.value : null } })
    }
  }

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={2}>
        <p>{label}</p>
      </Grid>
      <Grid item xs={10}>
        <div data-error-field={name}>
          {!isAsync ? (
            <ReactSelect
              name={name}
              label={label}
              value={fieldValue}
              options={options}
              isMulti={isMulti}
              isDisabled={isDisabled}
              isClearable
              onChange={handleSelectChange}
              onBlur={handleBlur}
            />
          ) : (
            <React.Fragment>
              <AsyncContentReferenceSelect
                name={name}
                value={values[name]?.display || (isMulti ? [] : null)}
                isMulti={isMulti}
                isDisabled={isDisabled}
                loadOptions={loadOptions}
                onChange={handleSelectChange}
                onBlur={handleBlur}
              />
            </React.Fragment>
          )}
        </div>
      </Grid>
    </Grid>
  )
}

/**
 * Determines if form values have changed from their initial state.
 *
 * This is a custom implementation to address several issues with Formik's built-in dirty state:
 * 1. Formik's dirty state was incorrectly marking the form as changed when:
 *    - Async fields (like series, interview hosts) were loaded with their initial values
 *    - Arrays (like similar videos) were reordered but contained the same values
 *    - The form was reinitialized with the same values after a save
 * 2. Formik's dirty state wasn't properly handling:
 *    - Complex nested objects in async fields
 *    - Array comparisons where order doesn't matter
 *    - The initial loading state where fields are populated asynchronously
 *
 * This implementation provides more reliable change detection by:
 * 1. Explicitly handling different field types (async fields, arrays, objects, primitives)
 * 2. Using proper comparison methods for each type
 * 3. Ensuring arrays are compared regardless of order
 * 4. Only considering changes after the initial load is complete
 *
 * @param {Object} values - Current form values
 * @param {Object} initialValues - Initial form values
 * @param {boolean} isInitialLoadComplete - Whether initial data loading is complete
 * @param {Object} content - Content object containing content type information
 * @returns {boolean} True if any field has changed from its initial value
 */
const hasFormChanges = (values, initialValues, isInitialLoadComplete, content) => {
  // Don't consider changes until initial load is complete
  if (!isInitialLoadComplete) return false

  return Object.keys(values).some((key) => {
    const currentValue = values[key]
    const initialValue = initialValues[key]

    // Handle special cases for async fields (like series, interview hosts, etc.)
    if (CONTENT_REFERENCE_FIELDS_ASYNC.get(content.get('contentType')).includes(key)) {
      if (!currentValue || !initialValue) return false
      if (Array.isArray(currentValue.value) && Array.isArray(initialValue.value)) {
        // Compare arrays of values regardless of order
        return (
          JSON.stringify(currentValue.value.sort()) !== JSON.stringify(initialValue.value.sort())
        )
      }
      return currentValue.value !== initialValue.value
    }

    // Handle arrays (like similar videos, categories, etc.)
    if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
      // Compare arrays regardless of order
      return JSON.stringify(currentValue.sort()) !== JSON.stringify(initialValue.sort())
    }

    // Handle objects (like complex field structures)
    if (
      typeof currentValue === 'object' &&
      currentValue !== null &&
      typeof initialValue === 'object' &&
      initialValue !== null
    ) {
      // Special case for media fields: only compare mediaId
      if (key === 'previewMedia' || key === 'featureMedia') {
        const currentMediaId = currentValue?.mediaId
        const initialMediaId = initialValue?.mediaId
        return currentMediaId !== initialMediaId
      }
      return JSON.stringify(currentValue) !== JSON.stringify(initialValue)
    }

    // Special case for media fields: treat '' and null as equivalent, and any transition from blank to value as dirty
    if (key === 'previewMedia' || key === 'featureMedia') {
      const normCurrent = currentValue === '' || currentValue === undefined ? null : currentValue
      const normInitial = initialValue === '' || initialValue === undefined ? null : initialValue
      // If both are null, not dirty
      if (!normCurrent && !normInitial) return false
      // If one is null and the other is a value, dirty
      if (!normCurrent || !normInitial) return true
      // If both are values, compare mediaIds ONLY
      const currentMediaId = normCurrent?.mediaId
      const initialMediaId = normInitial?.mediaId
      return currentMediaId !== initialMediaId
    }

    // Handle primitive values (strings, numbers, booleans)
    return currentValue !== initialValue
  })
}

/**
 * Processes a single form field value based on its type and requirements
 * @param {string} key - The field name
 * @param {any} value - The field value
 * @param {Object} initialValues - The initial form values
 * @returns {any} The processed value
 */
const processFieldValue = (key, value, initialValues) => {
  if (value === '' && initialValues[key] !== '') {
    return null
  }

  // Skip empty strings and unchanged textarea fields
  if (value === '' || (TEXTAREA_FIELDS.includes(key) && value === initialValues[key])) {
    return undefined
  }

  // Convert boolean fields
  if (BOOLEAN_FIELDS.includes(key)) {
    return Boolean(value)
  }

  // Convert number fields
  if (NUMBER_FIELDS.includes(key)) {
    return value ? Number(value) : null
  }

  // Handle series field - extract just the ID
  if (key === CONTENT_REFERENCE_TYPE_SERIES) {
    return value?.value ? Number(value.value) : null
  }

  // Keep filterType as string
  if (key === CONTENT_REFERENCE_TYPE_FILTER_OPTIONS) {
    return value || null
  }

  // Keep all other non-empty values
  return value
}

/**
 * Processes all form values, removing undefined values and mapping media fields to backend keys.
 * For previewMedia/featureMedia, only the mediaId is sent to the backend as previewMediaId/featureMediaId.
 */
const processFormValues = (values, initialValues) =>
  Object.entries(values).reduce((acc, [key, value]) => {
    if (key === 'previewMediaId') {
      acc.previewMediaId = value ? Number(value) : null
      return acc
    }
    if (key === 'featureMediaId') {
      acc.featureMediaId = value ? Number(value) : null
      return acc
    }
    const processedValue = processFieldValue(key, value, initialValues)
    if (processedValue !== undefined) {
      acc[key] = processedValue
    }
    return acc
  }, {})

/**
 * Creates a reference object for a single field
 * @param {string} type - The reference type
 * @param {any} value - The field value
 * @param {Object} content - The content object
 * @returns {Array} Array of reference objects
 */
const createReference = (type, value, content) => {
  if (!value) return []

  const contentType = getContentReferenceTypesContentDictionary().getIn([type, 'contentType', 0])

  const isAsync = CONTENT_REFERENCE_FIELDS_ASYNC.get(content.get('contentType')).includes(type)
  const isSingleSelect = SINGLE_SELECT_REFERENCE_TYPES.includes(type)

  // Handle async fields
  if (isAsync) {
    if (isSingleSelect) {
      const contentId = value.value ? parseInt(value.value, 10) : null
      return contentId && !Number.isNaN(contentId) ? [Map({ type, contentId, contentType })] : []
    }
    const values = value.value || []
    return values
      .map((id) => parseInt(id, 10))
      .filter((id) => !Number.isNaN(id))
      .map((contentId) => Map({ type, contentId, contentType }))
  }

  // Handle non-async fields
  if (isSingleSelect) {
    const contentId = parseInt(value, 10)
    return !Number.isNaN(contentId) ? [Map({ type, contentId, contentType })] : []
  }
  const valueArray = Array.isArray(value) ? value : [value]
  return valueArray
    .map((id) => {
      const numId = typeof id === 'object' ? id.value : id
      return parseInt(numId, 10)
    })
    .filter((id) => !Number.isNaN(id))
    .map((contentId) => Map({ type, contentId, contentType }))
}

/**
 * Custom hook for robust media info fetching.
 * NOTE: Multiple 404s may occur on page load due to hydration and repeated fetches for the same mediaId.
 * Consider deduplicating or debouncing these fetches in the future.
 */
function fetchSelectedMediaMetadata (values, mediaRecords, getDataList) {
  // Preview
  useEffect(() => {
    const { previewMediaId } = values
    if (previewMediaId) {
      getDataList({
        storeKey: MEDIA_STORE_KEY,
        options: createContentMediaOptions({
          content: Map({ previewMediaId }),
        }),
      })
    }
  }, [values.previewMediaId, getDataList])

  // Feature
  useEffect(() => {
    const { featureMediaId } = values
    if (featureMediaId) {
      getDataList({
        storeKey: MEDIA_STORE_KEY,
        options: createContentMediaOptions({
          content: Map({ featureMediaId }),
        }),
      })
    }
  }, [values.featureMediaId, getDataList])
}

function VideoDetail ({ applicationKey, storeKey }) {
  const auth = useSelector(selectAuth)
  const config = useSelector(selectConfig)
  const data = useSelector(selectData)
  const location = useSelector(selectLocation)
  const roles = useSelector(selectAuthRoles)
  const dispatch = useDispatch()

  // Memoize getDataList to ensure stable identity
  const getDataList = useCallback((...args) => dispatch(getDataListAction(...args)), [dispatch])

  const props = {
    applicationKey,
    storeKey,
    auth,
    config,
    data,
    location,
  }

  const {
    content,
    contentSeries,
    contentRecord,
    formErrors,
    processing,
    processingMedia,
    processingTranslations,
    recordsTranslated,
    contentReferenceFields,
    contentPolicyOptions,
    enableMarketingCenters,
  } = getState(props)

  // Get media info from Redux
  const mediaRecords = getRecords({ data, storeKey: MEDIA_STORE_KEY }) || List()

  // Remove debug logs
  const [isChangePrimaryLanguageDisabled, setIsChangePrimaryLanguageDisabled] = useState(true)
  const [hasSeriesChanged, setHasSeriesChanged] = useState(false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  const isVideo = content.get('contentType') === CONTENT_TYPE_VIDEO
  const isSeries = content.get('contentType') === CONTENT_TYPE_SERIES
  const isService = content.get('contentType') === CONTENT_TYPE_SERVICE
  const isPromotion = content.get('contentType') === CONTENT_TYPE_PROMOTION
  const isProduct = content.get('contentType') === CONTENT_TYPE_PRODUCT
  const isTag = content.get('contentType') === CONTENT_TYPE_TAG
  const isPerson = content.get('contentType') === CONTENT_TYPE_PERSON
  const isArticle = content.get('contentType') === CONTENT_TYPE_ARTICLE
  const contentReferenceFieldsMain = CONTENT_REFERENCE_FIELDS_MAIN.get(
    content.get('contentType'),
    List(),
  )
  const hasEditorPlusRole = roles.includes(ROLE_EDITOR_PLUS)
  const hasAdminRole = roles.includes(ROLE_SITE_ADMIN)
  const hasTranslations = recordsTranslated.size > 0

  const [loadContentReferenceList] = useLazyQuery(CONTENT_LIST_QUERY)
  const [getSeriesReferences] = useLazyQuery(GET_SERIES_REFERENCES)

  const contentReferenceQuery = async (value, bundle, contentType) => {
    const language = content.get('language') || 'en'
    const variables = {
      limit: 1000,
      order: [['title', 'asc']],
      language: [`${language}`, '_primary'],
      where: {
        ...(contentType && { contentType: { eq: contentType } }),
        ...(bundle && { bundle: { eq: bundle } }),
        ...(value && { title: { ilike: `%${value}%` } }),
      },
    }
    const queryResults = await loadContentReferenceList({ variables })
    return queryResults?.data?.contentList?.content || []
  }

  // Memoize initial values to prevent unnecessary recalculations
  const initialValues = useMemo(() => {
    const values = getInitialValues(content)

    // Ensure textarea fields always have string values for the UI
    TEXTAREA_FIELDS.forEach((field) => {
      values[field] = values[field] || '' // Convert null/undefined to empty string
    })

    // Normalize media fields to null if blank/undefined
    if (!values.previewMedia) values.previewMedia = null
    if (!values.featureMedia) values.featureMedia = null

    // Ensure reference values are preserved with display data
    const references = content.get('references')
    if (references) {
      // Group references by type to handle multi-select fields properly
      const groupedReferences = references.reduce((acc, ref) => {
        const type = ref.get('type')
        const contentId = ref.get('contentId')

        if (!acc[type]) {
          acc[type] = []
        }
        acc[type].push(contentId)
        return acc
      }, {})

      // Set values for each reference type
      Object.entries(groupedReferences).forEach(([type, contentIds]) => {
        const isAsync = CONTENT_REFERENCE_FIELDS_ASYNC.get(content.get('contentType')).includes(
          type,
        )
        const isMulti = !SINGLE_SELECT_REFERENCE_TYPES.includes(type)

        if (isAsync) {
          // Get display data from Redux
          const records = filterRecords({ data, storeKey }, (c) =>
            includes(contentIds, c.get('contentId')),
          )

          const displayData = records
            .map((record) => ({
              label: record.get('title'),
              value: record.get('contentId'),
            }))
            .toArray()

          if (displayData.length > 0) {
            values[type] = {
              value: isMulti ? contentIds : contentIds[0],
              display: isMulti ? displayData : displayData[0],
            }
          } else {
            // Fallback if no display data found
            values[type] = {
              value: isMulti ? contentIds : contentIds[0],
              display: isMulti ? [] : null,
            }
          }
        } else if (isMulti) {
          values[type] = values[type] || []
          contentIds.forEach((id) => {
            if (!values[type].includes(id)) {
              values[type].push(id)
            }
          })
        } else {
          // need this syntax because the other way destructures directly into an object property ([values[type]] = contentIds), could cause unexpected behavior
          const [first] = contentIds
          values[type] = first
        }
      })
    }

    return values
  }, [content, storeKey])

  const handleGrabSeriesMetadata = async (
    setFieldValue,
    seriesRefs,
    seriesId,
    setFormValues,
    formValues,
  ) => {
    try {
      // Extract the actual series ID from the value object if needed
      const actualSeriesId = typeof seriesId === 'object' ? seriesId.value : seriesId

      // 1. Fetch the latest references for the selected series
      const { data: seriesData } = await seriesRefs({
        variables: { seriesContentId: actualSeriesId },
      })

      if (!seriesData?.contentList?.content?.[0]?.contentReferencesFrom) {
        dispatch(showErrorToast('No metadata was found in the series data to populate fields.'))
        return
      }

      // 2. Extract and process the references
      const seriesReferences = seriesData.contentList.content[0].contentReferencesFrom
      const referenceTypes = getContentReferenceTypesContentDictionary()

      // Group references by type to handle multi-select fields properly
      const groupedReferences = seriesReferences.reduce((acc, ref) => {
        const referenceType = referenceTypes.findKey(
          (typeConfig) => typeConfig.get('bundle') === ref.contentTo[0].bundle,
        )

        if (
          !referenceType ||
          !REFERENCE_TYPE_KEYS_TO_POPULATE_FROM_METADATA.includes(referenceType)
        ) {
          return acc
        }

        if (!acc[referenceType]) {
          acc[referenceType] = []
        }

        acc[referenceType].push({
          value: ref.contentTo[0].contentId,
          label: ref.contentTo[0].title,
        })
        return acc
      }, {})

      // 3. Update form fields with grouped references
      const newFormValues = { ...formValues }
      Object.entries(groupedReferences).forEach(([type, options]) => {
        const isMulti = !SINGLE_SELECT_REFERENCE_TYPES.includes(type)
        const isAsync = CONTENT_REFERENCE_FIELDS_ASYNC.get(content.get('contentType')).includes(
          type,
        )

        if (isMulti) {
          if (isAsync) {
            // For async multi-select fields, store both values and display objects
            newFormValues[type] = {
              value: options.map((opt) => opt.value),
              display: options,
            }
          } else {
            // For non-async multi-select fields, just store the values array
            newFormValues[type] = options.map((opt) => opt.value)
          }
        } else if (isAsync) {
          // For async single-select fields, store both value and display
          newFormValues[type] = {
            value: options[0].value,
            display: {
              label: options[0].label,
              value: options[0].value,
            },
          }
        } else {
          // For non-async single-select fields, just store the numeric value
          newFormValues[type] = parseInt(options[0].value, 10)
        }
      })

      // Update all values at once to trigger proper form state update
      await setFormValues(newFormValues)

      // Set hasSeriesChanged to false after grabbing metadata
      setHasSeriesChanged(false)
    } catch (error) {
      dispatch(
        showErrorToast(
          'An error occurred while trying to grab metadata from series data. Please try again or contact support if the issue persists.',
        ),
      )
    }
  }

  // Add ref to access Formik methods
  const formikRef = useRef()

  // Add effect to handle initial load
  useEffect(() => {
    if (!processing && !processingMedia && !processingTranslations) {
      setIsInitialLoadComplete(true)
    }
  }, [processing, processingMedia, processingTranslations])

  // Add scroll function for error navigation
  const scrollToFieldByError = (errorFieldName) => {
    // Try to find the field using a simple, consistent approach
    let field = document.querySelector(`[data-error-field="${errorFieldName}"]`)

    // Fallback: try name attribute for regular form fields
    if (!field) {
      field = document.querySelector(`[name="${errorFieldName}"]`)
    }

    if (field) {
      field.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      // Add highlight effect using CSS class
      field.classList.add('error-highlight')

      // Clean up highlights after 2 seconds
      setTimeout(() => {
        field.classList.remove('error-highlight')
      }, 2000)
    }
  }

  if (content.isEmpty() && (processing || processingMedia || processingTranslations)) {
    return <ContentDetailLoader />
  }
  if (contentRecord.isEmpty()) {
    return <div>The content was not found</div>
  }

  const initialErrors = formErrors.toObject()
  const isViewOnlyMode = !getAuthPermissions(auth).includes(PERM_UPDATE_CONTENT)

  // const fetchTranslation = () => {
  //   dispatch(
  //     fetchTranslationAction({
  //       storeKey,
  //       content,
  //     }),
  //   )
  // }

  const changePrimaryLanguage = () => {
    if (isChangePrimaryLanguageDisabled) {
      dispatch(
        renderModalAction(CONFIRM_MODAL, {
          innerClass: 'modal__confirm',
          backgroundDismiss: true,
          onConfirm: () => setIsChangePrimaryLanguageDisabled(false),
          header: 'Unlock primary language field?',
          confirmText: 'Are you sure you want to unlock this field?',
          modalType: 'confirm',
        }),
      )
    } else {
      setIsChangePrimaryLanguageDisabled(true)
    }
  }

  const validationSchema = getValidationSchema(content)

  return (
    <Container className="content-detail" maxWidth={false}>
      <Formik
        innerRef={formikRef}
        initialErrors={initialErrors}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          // Process form values
          const processedValues = processFormValues(values, initialValues)

          // Build references array (primary-course is in the form via CONTENT_REFERENCE_FIELDS)
          const references = List(
            Object.entries(values)
              .filter(([key]) =>
                CONTENT_REFERENCE_FIELDS.get(content.get('contentType')).includes(key),
              )
              .flatMap(([type, value]) => createReference(type, value, content))
              .filter(Boolean),
          )

          // Update Redux
          const updatedContent = content.merge(Map(processedValues)).set('references', references)

          dispatch(
            persistDataAction({
              storeKey,
              applicationKey,
              record: updatedContent,
              options: createContentDetailViewOptions({ location }).set(
                'generatePath',
                processedValues.generatePath,
              ),
              processing: true,
            }),
          )

          // Reset form state
          setSubmitting(false)
          setIsChangePrimaryLanguageDisabled(true)
          setHasSeriesChanged(false)
        }}
        className="content-detail__form"
      >
        {({
          values,
          errors,
          touched,
          isValid,
          handleChange,
          handleBlur,
          setFieldValue,
          setValues,
          isSubmitting,
        }) => {
          fetchSelectedMediaMetadata(values, mediaRecords, getDataList)

          // Move hasChanges calculation here so values is defined
          const hasChanges = useMemo(
            () => hasFormChanges(values, initialValues, isInitialLoadComplete, content),
            [values, initialValues, isInitialLoadComplete, content],
          )

          const saveButtonValid = isValid

          // Use custom hook to manage display data for async content reference fields
          useAsyncContentReferenceDisplay({
            content,
            values,
            data,
            storeKey,
            setFieldValue,
            loadContentReferenceList,
          })

          return (
            <Form>
              <Container className="content-detail__section content-information" maxWidth={false}>
                <SectionAccordion title="Content Information" collapsed={false}>
                  <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={6}>
                      <Box display="flex" flexDirection="column" height="100%">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                          <p style={{ margin: 0 }}>
                            Content ID:{' '}
                            <span style={{ fontWeight: 600 }}>{content.get('contentId')}</span>
                          </p>
                          <p style={{ margin: 0 }}>
                            Content Type:{' '}
                            <span style={{ fontWeight: 600 }}>{content.get('contentType')}</span>
                          </p>
                        </div>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Title"
                          name="title"
                          type="text"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.title && Boolean(errors.title)}
                          helperText={touched.title && errors.title}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                          data-error-field="title"
                          style={{ marginTop: 16 }}
                        />
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            flexWrap: 'nowrap',
                            marginTop: 10,
                            marginBottom: 8,
                          }}
                        >
                          <span style={{ fontSize: 14, color: '#333' }}>Language:</span>
                          <Chip
                            label={getLanguageLabel(content.get('language'))}
                            size="small"
                            variant="outlined"
                            className={`language-chip ${getLanguageChipClass(content.get('language'))}`}
                            sx={{ color: '#333', '&:hover': { opacity: 0.8 } }}
                          />
                          <span
                            style={{ marginLeft: 16, marginRight: 4, color: '#666', fontSize: 14 }}
                          >
                            Primary Language
                          </span>
                          {isChangePrimaryLanguageDisabled ? (
                            <>
                              <Chip
                                label={getLanguageLabel(content.get('primaryLanguage'))}
                                size="small"
                                variant="outlined"
                                className={`language-chip ${getLanguageChipClass(content.get('primaryLanguage'))}`}
                                sx={{ color: '#333', '&:hover': { opacity: 0.8 } }}
                              />
                              {(hasEditorPlusRole || hasAdminRole) && !hasTranslations && (
                                <div
                                  className="content-detail__svg"
                                  onClick={changePrimaryLanguage}
                                  style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    marginLeft: 8,
                                    marginTop: 0,
                                  }}
                                >
                                  <LockOutlinedIcon fontSize="medium" color="action" />
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <MuiSelectWrapper
                                className="content-detail__input"
                                name="primaryLanguage"
                                id="primaryLanguage"
                                label="Primary language"
                                value={values.primaryLanguage}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={false}
                              >
                                {renderLanguageOptions()}
                              </MuiSelectWrapper>
                              <div
                                className="content-detail__svg"
                                onClick={changePrimaryLanguage}
                                style={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '100%',
                                  marginTop: -15,
                                }}
                              >
                                <LockOpenOutlinedIcon fontSize="medium" color="primary" />
                              </div>
                            </>
                          )}
                        </div>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Path"
                          name="path"
                          type="text"
                          value={values.path}
                          disabled={
                            isViewOnlyMode ||
                            values.generatePath ||
                            content.get('primaryLanguage') !== content.get('language')
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.path && Boolean(errors.path)}
                          helperText={touched.path && errors.path}
                          fullWidth
                          variant="outlined"
                          style={{ marginTop: 8 }}
                        />
                        <MuiCheckbox
                          label="Generate path for me (only applies to source language)"
                          name="generatePath"
                          onChange={handleChange}
                          checked={values.generatePath}
                          disabled={
                            isViewOnlyMode ||
                            content.get('primaryLanguage') !== content.get('language')
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box display="flex" flexDirection="column" height="100%" marginTop="12px">
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16,
                            marginTop: 8,
                            marginBottom: 4,
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <MuiTextFieldWrapper
                              className="content-detail__input"
                              label="Publish On Date"
                              name="publishedAtDate"
                              type="datetime-local"
                              value={
                                Number.isNaN(Date.parse(values.publishedAtDate))
                                  ? ''
                                  : formatInTimeZone(
                                      values.publishedAtDate,
                                      TIMEZONE_MOUNTAIN,
                                      "yyyy-MM-dd'T'HH:mm",
                                    )
                              }
                              onChange={(e) => {
                                const {
                                  target: { name, value },
                                } = e
                                const modifiedE = {
                                  target: {
                                    name,
                                    value: Number.isNaN(Date.parse(value))
                                      ? ''
                                      : zonedTimeToUtc(value, TIMEZONE_MOUNTAIN),
                                  },
                                }
                                handleChange(modifiedE)
                              }}
                              error={touched.publishedAtDate && Boolean(errors.publishedAtDate)}
                              helperText={touched.publishedAtDate && errors.publishedAtDate}
                              disabled={isViewOnlyMode}
                              fullWidth
                              variant="outlined"
                              style={{ margin: '20px 0 0 0' }}
                            />
                            <div
                              style={{ fontSize: 12, color: '#666', marginTop: 4, marginBottom: 0 }}
                            >
                              (Date/Times are displayed in Mountain Time)
                            </div>
                          </div>
                          <Chip
                            label={
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingLeft: 15,
                                }}
                              >
                                <MuiCheckbox
                                  label=""
                                  name="published"
                                  onChange={handleChange}
                                  checked={values.published}
                                  disabled={isViewOnlyMode}
                                  sx={{
                                    padding: 0,
                                    marginTop: '6px', // Lower the checkbox
                                    '& .MuiFormControlLabel-root': {
                                      margin: 0,
                                    },
                                    '& .MuiCheckbox-root': {
                                      padding: 0,
                                      color: 'white',
                                      '&.Mui-checked': {
                                        color: 'white',
                                      },
                                    },
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    marginLeft: -7,
                                    marginBottom: '-2px', // Lower the text a bit
                                  }}
                                >
                                  Published
                                </span>
                              </div>
                            }
                            sx={{
                              background: values.published
                                ? 'linear-gradient(135deg, #81c784 0%, #a5d6a7 100%)'
                                : '#e57373',
                              color: 'white',
                              borderRadius: '20px',
                              height: '56px',
                              alignSelf: 'flex-start',
                              marginTop: '16px',
                              cursor: isViewOnlyMode ? 'default' : 'pointer',
                              '& .MuiChip-label': {
                                padding: '8px 16px',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                              },
                              '&:hover': {
                                background: (() => {
                                  if (isViewOnlyMode) {
                                    return values.published
                                      ? 'linear-gradient(135deg, #81c784 0%, #a5d6a7 100%)'
                                      : '#e57373'
                                  }
                                  return values.published
                                    ? 'linear-gradient(135deg, #66bb6a 0%, #81c784 100%)'
                                    : '#da190b'
                                })(),
                              },
                            }}
                            variant="filled"
                            onClick={
                              isViewOnlyMode
                                ? undefined
                                : () => {
                                    handleChange({
                                      target: {
                                        name: 'published',
                                        checked: !values.published,
                                      },
                                    })
                                  }
                            }
                          />
                        </div>
                        <input type="hidden" id="timezone" name="timezone" value="Denver/America" />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <MuiCheckbox
                            label="Hide from Search"
                            name="contentSearchNoIndex"
                            onChange={handleChange}
                            checked={values.contentSearchNoIndex}
                            disabled={isViewOnlyMode}
                            style={{ marginBottom: 2 }}
                          />
                          <MuiCheckbox
                            label="Hide from Recommendations"
                            name="excludeFromRecommendations"
                            onChange={handleChange}
                            checked={values.excludeFromRecommendations}
                            disabled={isViewOnlyMode}
                          />
                        </div>
                        {(isService || isSeries) && (
                          <MuiSelectWrapper
                            className="content-detail__input"
                            id="contentPolicyPolicyId"
                            name="contentPolicyPolicyId"
                            label="Content Policy"
                            value={values.contentPolicyPolicyId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            style={{ marginTop: 8 }}
                          >
                            {renderContentPolicyOptions(contentPolicyOptions)}
                          </MuiSelectWrapper>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <div style={{ marginBottom: 16, fontWeight: 500 }}>Translations</div>
                      {/* <MuiButton onClick={fetchTranslation}>Fetch Existing Translation</MuiButton> */}
                      <ul style={{ marginTop: 0 }}>
                        {getLanguageOptionsSupported().reduce((acc, v) => {
                          const language = v.get('value')
                          const isPrimary = content.get('primaryLanguage') === language
                          // Skip current content
                          if (language === content.get('language')) {
                            return acc
                          }
                          let languageText = getLanguageLabel(language)
                          if (isPrimary) {
                            languageText += ' (Primary)'
                          }
                          const recordTranslated = recordsTranslated.find(
                            (r) => r.get('language') === language,
                          )
                          let href = `/content/create?contentId=${content.get('contentId')}&contentType=${content.get('contentType')}`
                          let text = 'Create Translation'
                          let chipClass = 'language-chip--default' // Generic gray color for non-existent translations
                          if (isViewOnlyMode) {
                            href = undefined
                            text = 'Does not exist'
                          }
                          if (recordTranslated) {
                            href = `/content/${recordTranslated.get('id')}`
                            text = recordTranslated.get('title')
                            chipClass = getLanguageChipClass(language) // Use language-specific color for existing translations
                          }

                          // Skip pushing if content type is PRODUCT and recordTranslated is false
                          const excludedContentTypes = [
                            CONTENT_TYPE_BONUS_PACKAGE,
                            CONTENT_TYPE_PRODUCT,
                          ]
                          if (
                            excludedContentTypes.includes(content.get('contentType')) &&
                            !recordTranslated
                          ) {
                            return acc
                          }

                          return acc.push(
                            <li key={`content-information-translation-${v.get('value')}`}>
                              {href ? (
                                <Link href={href}>
                                  <Chip
                                    label={languageText}
                                    size="small"
                                    variant="outlined"
                                    className={`language-chip ${chipClass}`}
                                    sx={{
                                      mr: 1,
                                      mb: 1,
                                      color: '#333',
                                      '&:hover': {
                                        opacity: 0.8,
                                      },
                                    }}
                                  />
                                  <span
                                    className={`translation-link__text ${text === 'Create Translation' ? 'translation-link__text--create' : 'translation-link__text--exists'}`}
                                  >
                                    {text}
                                  </span>
                                </Link>
                              ) : (
                                <React.Fragment>
                                  <Chip
                                    label={languageText}
                                    size="small"
                                    variant="outlined"
                                    className={`language-chip ${chipClass}`}
                                    sx={{
                                      mr: 1,
                                      mb: 1,
                                      color: '#333',
                                      '&:hover': {
                                        opacity: 0.8,
                                      },
                                    }}
                                  />
                                  <span className="translation-link__text translation-link__text--disabled">
                                    {text}
                                  </span>
                                </React.Fragment>
                              )}
                            </li>,
                          )
                        }, List())}
                      </ul>
                    </Grid>
                    <Grid item xs={6}></Grid>
                  </Grid>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <TextareaWithCounter
                        className="content-detail__input"
                        label="Short description"
                        name="shortDescription"
                        value={values.shortDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isViewOnlyMode}
                        maxValue={150}
                        error={touched.shortDescription && Boolean(errors.shortDescription)}
                        helperText={touched.shortDescription && errors.shortDescription}
                      />
                      <TextInputFormatSelect
                        className="content-detail__input"
                        label="Short description format"
                        value={values.shortDescriptionFormat}
                        name="shortDescriptionFormat"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isViewOnlyMode}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <TextareaWithCounter
                        className="content-detail__input"
                        label="Long description"
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isViewOnlyMode}
                        maxValue={4000}
                        rows={10}
                        error={touched.description && Boolean(errors.description)}
                        helperText={touched.description && errors.description}
                      />
                      <TextInputFormatSelect
                        className="content-detail__input"
                        label="Long description format"
                        name="descriptionFormat"
                        value={values.descriptionFormat}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <TextareaWithCounter
                        className="content-detail__input"
                        label="Third Party Description"
                        name="thirdPartyDescription"
                        value={values.thirdPartyDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.thirdPartyDescription && Boolean(errors.thirdPartyDescription)
                        }
                        helperText={touched.thirdPartyDescription && errors.thirdPartyDescription}
                        disabled={isViewOnlyMode}
                        maxValue={255}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <MuiTextFieldWrapper
                        className="content-detail__input"
                        label="Created at"
                        name="createdAt"
                        type="text"
                        value={formatInTimeZone(values.createdAt, TIMEZONE_MOUNTAIN, 'yyyy-MM-dd')}
                        disabled={true}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MuiTextFieldWrapper
                        className="content-detail__input"
                        label="Updated at"
                        name="updatedAt"
                        type="text"
                        value={formatInTimeZone(values.updatedAt, TIMEZONE_MOUNTAIN, 'yyyy-MM-dd')}
                        disabled={true}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  {(isVideo || isSeries) && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <MuiSelectWrapper
                          className="content-detail__input"
                          id="productType"
                          name="productType"
                          label="Product type"
                          value={values.productType || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          disabled={isViewOnlyMode}
                        >
                          {renderProductTypeOptions(content)}
                        </MuiSelectWrapper>
                      </Grid>
                      <Grid item xs={6}>
                        <MuiSelectWrapper
                          className="content-detail__input"
                          name="displayType"
                          id="displayType"
                          label="Display type"
                          value={values.displayType}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          disabled={isViewOnlyMode}
                        >
                          {renderDisplayTypeOptions(content.get('contentType'))}
                        </MuiSelectWrapper>
                      </Grid>
                    </Grid>
                  )}
                  {isSeries && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <ProductionStatusSelect
                          className="content-detail__input"
                          label="Production status"
                          name="productionStatus"
                          id="productionStatus"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.productionStatus}
                          disabled={isViewOnlyMode}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {(isVideo || isArticle) && (
                    <React.Fragment>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                          <MuiTextFieldWrapper
                            label="Series"
                            value={createSeriesText(contentSeries)}
                            onChange={() => {}}
                            name="seriesContentId"
                            disabled={true}
                            fullWidth
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <MuiTextFieldWrapper
                            className="content-detail__input"
                            label="Season"
                            name="seasonNumber"
                            type="text"
                            value={values.seasonNumber || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={isEpisode(values)}
                            disabled={isViewOnlyMode || !isEpisode(values)}
                            error={touched.seasonNumber && Boolean(errors.seasonNumber)}
                            helperText={touched.seasonNumber && errors.seasonNumber}
                            fullWidth
                            variant="outlined"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <MuiTextFieldWrapper
                            className="content-detail__input"
                            label="Episode"
                            name="episodeNumber"
                            type="text"
                            value={values.episodeNumber || ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={isEpisode(values)}
                            disabled={isViewOnlyMode || !isEpisode(values)}
                            error={touched.episodeNumber && Boolean(errors.episodeNumber)}
                            helperText={touched.episodeNumber && errors.episodeNumber}
                            fullWidth
                            variant="outlined"
                          />
                        </Grid>
                      </Grid>
                    </React.Fragment>
                  )}
                  {isVideo && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Director"
                          name="director"
                          type="text"
                          value={values.director}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.director && Boolean(errors.director)}
                          helperText={touched.director && errors.director}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Producer"
                          name="producer"
                          type="text"
                          value={values.producer}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.producer && Boolean(errors.producer)}
                          helperText={touched.producer && errors.producer}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  )}
                  {(isVideo || isSeries || isArticle) && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Writer"
                          name="writer"
                          type="text"
                          value={values.writer}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.writer && Boolean(errors.writer)}
                          helperText={touched.writer && errors.writer}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Studio"
                          name="studio"
                          type="text"
                          value={values.studio}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.studio && Boolean(errors.studio)}
                          helperText={touched.studio && errors.studio}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  )}
                  {(isVideo || isSeries || isArticle) && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Copyright"
                          name="copyright"
                          type="text"
                          value={values.copyright}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.copyright && Boolean(errors.copyright)}
                          helperText={touched.copyright && errors.copyright}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="Instructor"
                          name="instructor"
                          type="text"
                          value={values.instructor}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.instructor && Boolean(errors.instructor)}
                          helperText={touched.instructor && errors.instructor}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  )}
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <MuiTextFieldWrapper
                        className="content-detail__input"
                        label="Cast (separated by commas)"
                        name="cast"
                        type="text"
                        value={values.cast}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.cast && Boolean(errors.cast)}
                        helperText={touched.cast && errors.cast}
                        disabled={isViewOnlyMode}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </SectionAccordion>
              </Container>
              {(isTag || isPerson) && (
                <Container className="content-detail__section content-field-type" maxWidth={false}>
                  <SectionAccordion title="Filter Type" collapsed>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <ReactSelect
                          className="content-detail__select"
                          name="filterType"
                          id="filterType"
                          value={renderFilterTypeOptions(
                            content.get('contentType'),
                            enableMarketingCenters,
                          ).filter(
                            (item) =>
                              !isEmpty(values.filterType) &&
                              includes(values.filterType.split(','), item.value),
                          )}
                          options={renderFilterTypeOptions(
                            content.get('contentType'),
                            enableMarketingCenters,
                          )}
                          label="Filter Type"
                          onChange={(selectedOptions) => {
                            const name = 'filterType'
                            const e = {
                              target: {
                                name,
                                value: isPerson
                                  ? map(selectedOptions, 'value').join(',')
                                  : selectedOptions.value,
                              },
                            }
                            handleChange(e)
                          }}
                          isMulti={isPerson}
                        ></ReactSelect>
                      </Grid>
                      <Grid item xs={6}>
                        <ReactSelect
                          className="content-detail__select"
                          name="bundle"
                          id="bundle"
                          value={renderBundleOptions(content.get('contentType')).filter(
                            (item) => values.bundle === item.value,
                          )}
                          options={renderBundleOptions(content.get('contentType'))}
                          label="Bundle"
                          isDisabled={isPerson}
                          onChange={(selectedOptions) => {
                            const name = 'bundle'
                            const { value } = selectedOptions
                            const e = { target: { name, value } }
                            handleChange(e)
                          }}
                        ></ReactSelect>
                      </Grid>
                    </Grid>
                  </SectionAccordion>
                </Container>
              )}

              {/* AI Tools Section - contains both metadata generation and marketing plan */}
              {isVideo && (
                <Container className="content-detail__section ai-tools" maxWidth={false}>
                  <SectionAccordion title="✨ AI Tools" collapsed={true}>
                    <AIMetadataGeneration
                      content={content}
                      isViewOnlyMode={isViewOnlyMode}
                      featureMediaId={values.featureMediaId}
                      setFieldValue={setFieldValue}
                    />

                    {/* Divider between the two tools */}
                    <Box sx={{ my: 4, borderBottom: '2px solid #e0e0e0' }} />

                    <AIMarketingPlanGeneration
                      content={content}
                      isViewOnlyMode={isViewOnlyMode}
                      featureMediaId={values.featureMediaId}
                    />
                  </SectionAccordion>
                </Container>
              )}

              {isVideo && (
                <PrimaryCourseSection
                  videoId={content.get('id')}
                  videoLanguage={content.get('language')}
                  videoContentId={content.get('contentId')}
                  videoContentType={content.get('contentType')}
                  disabled={isViewOnlyMode}
                  mainFormIsDirty={hasChanges}
                />
              )}

              <Container className="content-detail__section content-references" maxWidth={false}>
                <SectionAccordion title="Content References" collapsed={false}>
                  {contentReferenceFields
                    .filter((f) => contentReferenceFieldsMain.includes(f.get('name')))
                    .map((field) => {
                      const isSeriesField = field.get('name') === CONTENT_REFERENCE_TYPE_SERIES
                      return (
                        <React.Fragment key={field.get('name')}>
                          {renderContentReferenceField(field, values, errors, {
                            isDisabled: isViewOnlyMode,
                            handleChange,
                            contentReferenceQuery,
                            setFieldValue: (name, value) => {
                              setFieldValue(name, value)
                              // Track series changes
                              if (name === CONTENT_REFERENCE_TYPE_SERIES) {
                                // Use setTimeout to avoid setState in render
                                setTimeout(() => {
                                  setHasSeriesChanged(true)
                                }, 0)
                              }
                            },
                          })}
                          {isSeriesField &&
                            values.series &&
                            !isViewOnlyMode &&
                            hasSeriesChanged && (
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={2}></Grid>
                                <Grid item xs={10}>
                                  <div className="content-detail__series-metadata">
                                    <MuiButton
                                      onClick={() =>
                                        handleGrabSeriesMetadata(
                                          setFieldValue,
                                          getSeriesReferences,
                                          values.series,
                                          setValues,
                                          values,
                                        )
                                      }
                                    >
                                      Copy Metadata from Series
                                    </MuiButton>
                                    <Tooltip
                                      title={
                                        <React.Fragment>
                                          This button will look up the following Content References
                                          from the series and automatically apply them to this
                                          episode:
                                          <br />
                                          • Admin Category
                                          <br />
                                          • Category
                                          <br />
                                          • Rainbow Category
                                          <br />
                                          • Centers
                                          <br />
                                          • Content Origin
                                          <br />
                                          • Genres
                                          <br />
                                          • Interview Host
                                          <br />• Third Party Genre
                                        </React.Fragment>
                                      }
                                    >
                                      <IconButton size="small">
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </div>
                                </Grid>
                              </Grid>
                            )}
                        </React.Fragment>
                      )
                    })}
                </SectionAccordion>
              </Container>
              <Container
                className="content-detail__section content-references-extended"
                maxWidth={false}
              >
                <SectionAccordion title="Content References Extended" collapsed={false}>
                  {/* Render all non-yoga/meditation extended fields full width */}
                  {contentReferenceFields
                    .filter((f) => {
                      const name = f.get('name')
                      const yogaMeditation = [
                        ...YOGA_REFERENCE_FIELDS.toArray(),
                        ...MEDITATION_REFERENCE_FIELDS.toArray(),
                      ]
                      return (
                        name !== CONTENT_REFERENCE_TYPE_PRIMARY_COURSE &&
                        !contentReferenceFieldsMain.includes(name) &&
                        !CONTENT_REFERENCE_FIELDS_YORE.includes(name) &&
                        !yogaMeditation.includes(name)
                      )
                    })
                    .map((field) => (
                      <React.Fragment key={field.get('name')}>
                        {renderContentReferenceField(field, values, errors, {
                          isDisabled: isViewOnlyMode,
                          handleChange,
                          contentReferenceQuery,
                          setFieldValue,
                        })}
                      </React.Fragment>
                    ))}
                  {/* Yoga/Meditation table-like grid */}
                  <Box sx={{ width: '100%' }}>
                    {YOGA_REFERENCE_FIELDS.map((yogaField, idx) => (
                      <Grid container spacing={1} alignItems="center" key={yogaField}>
                        <Grid item xs={12} md={6}>
                          {renderContentReferenceField(
                            contentReferenceFields.find((f) => f.get('name') === yogaField),
                            values,
                            errors,
                            {
                              isDisabled: isViewOnlyMode,
                              handleChange,
                              contentReferenceQuery,
                              setFieldValue,
                            },
                          )}
                        </Grid>
                        <Grid item xs={12} md={6}>
                          {/* Only render meditation field if it exists for this row */}
                          {MEDITATION_REFERENCE_FIELDS.get(idx)
                            ? renderContentReferenceField(
                                contentReferenceFields.find(
                                  (f) => f.get('name') === MEDITATION_REFERENCE_FIELDS.get(idx),
                                ),
                                values,
                                errors,
                                {
                                  isDisabled: isViewOnlyMode,
                                  handleChange,
                                  contentReferenceQuery,
                                  setFieldValue,
                                },
                              )
                            : null}
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                </SectionAccordion>
              </Container>

              <Container
                className="content-detail__section content-references-yore"
                maxWidth={false}
              >
                <SectionAccordion title="Content References of Yore" collapsed={true}>
                  {contentReferenceFields
                    .filter((f) => CONTENT_REFERENCE_FIELDS_YORE.includes(f.get('name')))
                    .map((field) => (
                      <React.Fragment key={field.get('name')}>
                        {renderContentReferenceField(field, values, errors, {
                          isDisabled: isViewOnlyMode,
                          handleChange,
                          contentReferenceQuery,
                          setFieldValue,
                        })}
                      </React.Fragment>
                    ))}
                </SectionAccordion>
              </Container>
              {isProduct && (
                <Container
                  id="product-references"
                  className="content-detail__section product-references"
                  maxWidth={false}
                >
                  <SectionAccordion title="Content That References This Product" collapsed>
                    <ProductReferencesList content={content} isViewOnlyMode={isViewOnlyMode} />
                  </SectionAccordion>
                </Container>
              )}
              {(isVideo || isSeries || isPromotion) && (
                <VideoContentMedia
                  content={content}
                  onChange={handleChange}
                  disabled={isViewOnlyMode}
                  values={values}
                  collapsed={false}
                />
              )}

              {isSeries && <ContentEpisodes disabled={isViewOnlyMode} />}

              {(isPromotion || isProduct) && (
                <Container className="content-detail__section cta" maxWidth={false}>
                  <SectionAccordion title="CTA" collapsed={false}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="CTA Label"
                          name="ctaLabel"
                          value={values.ctaLabel}
                          onChange={handleChange}
                          error={touched.ctaLabel && Boolean(errors.ctaLabel)}
                          helperText={touched.ctaLabel && errors.ctaLabel}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="CTA Path"
                          name="ctaPath"
                          value={values.ctaPath}
                          onChange={handleChange}
                          error={touched.ctaPath && Boolean(errors.ctaPath)}
                          helperText={touched.ctaPath && errors.ctaPath}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <MuiTextFieldWrapper
                          className="content-detail__input"
                          label="CTA Deeplink Path"
                          name="ctaDeeplinkPath"
                          value={values.ctaDeeplinkPath}
                          onChange={handleChange}
                          error={touched.ctaDeeplinkPath && Boolean(errors.ctaDeeplinkPath)}
                          helperText={touched.ctaDeeplinkPath && errors.ctaDeeplinkPath}
                          disabled={isViewOnlyMode}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </SectionAccordion>
                </Container>
              )}

              <ContentImages content={content} disabled={isViewOnlyMode} collapsed={false} />

              <Container className="content-detail__section seo" maxWidth={false}>
                <SectionAccordion title="SEO" collapsed={false}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <MuiTextFieldWrapper
                        className="content-detail__input"
                        label="SEO Title"
                        name="seoTitle"
                        value={values.seoTitle}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.seoTitle && Boolean(errors.seoTitle)}
                        helperText={touched.seoTitle && errors.seoTitle}
                        disabled={isViewOnlyMode}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <MuiTextFieldWrapper
                        className="content-detail__input"
                        label="SEO Description"
                        name="seoDescription"
                        value={values.seoDescription}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.seoDescription && Boolean(errors.seoDescription)}
                        helperText={touched.seoDescription && errors.seoDescription}
                        disabled={isViewOnlyMode}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <MuiTextFieldWrapper
                        className="content-detail__input"
                        label="SEO Keywords (separated by commas)"
                        name="seoKeywords"
                        value={values.seoKeywords}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.seoKeywords && Boolean(errors.seoKeywords)}
                        helperText={touched.seoKeywords && errors.seoKeywords}
                        disabled={isViewOnlyMode}
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}>
                      <MuiCheckbox
                        name="seoRobotsNoFollow"
                        label="SEO Robots - No Follow"
                        onChange={handleChange}
                        checked={values.seoRobotsNoFollow}
                        disabled={isViewOnlyMode}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}>
                      <MuiCheckbox
                        name="seoRobotsNoIndex"
                        label="SEO Robots - No Index"
                        onChange={handleChange}
                        checked={values.seoRobotsNoIndex}
                        disabled={isViewOnlyMode}
                      />
                    </Grid>
                  </Grid>
                </SectionAccordion>
              </Container>

              {isTag && (hasEditorPlusRole || hasAdminRole) && (
                <Container className="content-detail__section filtered-display" maxWidth={false}>
                  <SectionAccordion title="Filtered Display Options">
                    <Grid container spacing={2} alignItems="center">
                      {getFilteredDisplayOptions().map((option) => (
                        <Grid item xs={3} key={option.get('label')}>
                          <MuiCheckbox
                            label={option.get('label')}
                            name="listCriteria.criteria.all"
                            value={option.get('value')}
                            onChange={(e) => {
                              const { checked } = e.target
                              const [contentType, productType] = option.get('value')
                              const currentAll = values.listCriteria?.criteria?.all || []

                              let newAll
                              if (checked) {
                                const newItem = { contentType }
                                if (productType) {
                                  newItem.productType = productType
                                }
                                newAll = [...currentAll, newItem]
                              } else {
                                newAll = currentAll.filter(
                                  (item) =>
                                    !(
                                      item.contentType === contentType &&
                                      item.productType === productType
                                    ),
                                )
                              }

                              handleChange({
                                target: {
                                  name: 'listCriteria',
                                  value: {
                                    criteria: {
                                      all: newAll,
                                    },
                                  },
                                },
                              })
                            }}
                            checked={values.listCriteria?.criteria?.all?.some(
                              (item) =>
                                item.contentType === option.get('value')[0] &&
                                item.productType === option.get('value')[1],
                            )}
                            disabled={isViewOnlyMode}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </SectionAccordion>
                </Container>
              )}

              <ContentActions
                content={content}
                errors={errors}
                isSubmitting={isSubmitting}
                processing={processing}
                isValid={saveButtonValid}
                isDirty={hasChanges}
                onErrorClick={scrollToFieldByError}
              />
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}

VideoDetail.propTypes = {
  applicationKey: PropTypes.string.isRequired,
  storeKey: PropTypes.string.isRequired,
}

VideoDetail.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
  applicationKey: CONTENT_DETAIL_APPLICATION_KEY,
}

export default VideoDetail
